import { canEditOrder } from '@ldt/api/order_loader/clinic';
import {
  isSpecimenInformationMissing,
  SPECIMEN_INFORMATION_MISSING_MESSAGE,
} from '@ldt/features/orders/components/specimen_information_form_section';
import { hasLdtValidationErrors } from '@ldt/features/orders/utils/has_ldt_validation_errors';
import {
  CLINICAL_VARIABLES_MISSING_MESSAGE,
  getClinicRequiredUploadMissingInfoMessages,
  getPathologyLabMissingInfoMessages,
  isClinicalVariablesMissing,
  isOrderingProviderMissing,
  ORDERING_PROVIDER_MISSING_MESSAGE,
} from '@ldt/features/orders/utils/order_missing_info';
import type {
  CheckOrderValidationErrorsContext,
  GetAvailableActionsContext,
  LdtOrderAction,
} from '@ldt/workflows/types';
import { LdtOrderAction as Action } from '@ldt/workflows/types';
import { findMostRecentSlideImage } from 'src/distribution_platforms/features/utils/slide_image_utils';
import {
  ArteraFaxStatus,
  FaxFieldsFragment,
  LdtOrderDetailFieldsFragment,
  OrderStatus,
} from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import { isCustomerSuccessOrSalesUser } from 'src/utils';
import {
  isClinicAdmin,
  isCustomerSuccess,
  isOrderingPhysician,
  isSalesUser,
} from 'src/utils';

function getDraftActions(canEdit: boolean): LdtOrderAction[] {
  if (canEdit) return [Action.EditOrder];
  return [];
}

function getQcPendingActions(
  missingInfo: string[],
  hasValidationErrors: boolean,
): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(Action.DisplayRequisition, Action.UpdateFiles);
  } else if (isCustomerSuccessOrSalesUser()) {
    // Only show QC Approve if there are no missing information messages
    if (!hasValidationErrors && missingInfo.length === 0)
      actions.push(Action.ApproveQC);
    actions.push(Action.EditOrder, Action.Cancel);
  }
  return actions;
}

function getQcApprovedActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(Action.DisplayRequisition);
  } else if (isCustomerSuccessOrSalesUser()) {
    actions.push(
      Action.DisplayRequisition,
      Action.RevertToQcPending,
      Action.Cancel,
    );
  }
  return actions;
}

function getLabProcessingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin())
    actions.push(Action.DisplayRequisition);
  else if (isCustomerSuccessOrSalesUser())
    actions.push(
      Action.DisplayRequisition,
      Action.RevertToQcPending,
      Action.Cancel,
    );
  return actions;
}

function getReportPendingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin())
    actions.push(Action.DisplayRequisition);
  else if (isCustomerSuccessOrSalesUser())
    actions.push(
      Action.DisplayRequisition,
      Action.RejectDraftReport,
      Action.Cancel,
    );
  return actions;
}

function getReportReadyActions(
  showFaxTestReportToPathologyLab: boolean,
): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayPatientReport,
      Action.ShareTestReport,
    );
  } else if (isCustomerSuccess()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayPatientReport,
      Action.DisplayDeIdentifiedReport,
      Action.ReviseReportMutation,
    );
    if (showFaxTestReportToPathologyLab) {
      actions.push(Action.FaxTestReportToPathologyLabMutation);
    }
  } else if (isSalesUser()) {
    actions.push(Action.DisplayRequisition, Action.DisplayDeIdentifiedReport);
    if (showFaxTestReportToPathologyLab) {
      actions.push(Action.FaxTestReportToPathologyLabMutation);
    }
  }
  return actions;
}

function getReportFaxPendingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayPatientReport,
      Action.ShareTestReport,
    );
  } else if (isCustomerSuccess()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayPatientReport,
      Action.DisplayDeIdentifiedReport,
      Action.ReviseReportMutation,
      Action.FaxTestReportToPathologyLabMutation,
    );
  } else if (isSalesUser()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayDeIdentifiedReport,
      Action.FaxTestReportToPathologyLabMutation,
    );
  }
  return actions;
}

function getReportFaxFailedActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayPatientReport,
      Action.ShareTestReport,
    );
  } else if (isCustomerSuccess()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayPatientReport,
      Action.DisplayDeIdentifiedReport,
      Action.RefaxReportToOrderingProvider,
      Action.FaxTestReportToPathologyLabMutation,
      Action.ReviseReportMutation,
    );
  } else if (isSalesUser()) {
    actions.push(
      Action.DisplayRequisition,
      Action.DisplayDeIdentifiedReport,
      Action.RefaxReportToOrderingProvider,
      Action.FaxTestReportToPathologyLabMutation,
    );
  }
  return actions;
}

function getPathologyFaxPendingActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [Action.DisplayRequisition];
  if (isCustomerSuccessOrSalesUser())
    actions.push(Action.Cancel, Action.RevertToQcPending);
  return actions;
}

function getPathologyFaxFailedActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [Action.DisplayRequisition];
  if (isCustomerSuccessOrSalesUser())
    actions.push(
      Action.RefaxPathologyFiles,
      Action.Cancel,
      Action.RevertToQcPending,
    );
  return actions;
}

function getOtherProcessingStatusActions(): LdtOrderAction[] {
  return [Action.DisplayRequisition];
}

function getSlideImageQualityControlPendingActions(
  order: LdtOrderDetailFieldsFragment,
): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(Action.DisplayRequisition);
  } else if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.ImageQcApprove);
    actions.push(
      Action.ImageQcReject,
      Action.DisplayRequisition,
      Action.Cancel,
    );
    if (findMostRecentSlideImage(order)) {
      actions.push(Action.DownloadSlideImageQuery);
    }
  }
  return actions;
}

function getSlideImageQualityControlFailedActions(
  order: LdtOrderDetailFieldsFragment,
): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(Action.DisplayRequisition);
  } else if (isCustomerSuccessOrSalesUser()) {
    actions.push(Action.DisplayRequisition, Action.Cancel);
    if (findMostRecentSlideImage(order)) {
      actions.push(Action.DownloadSlideImageQuery);
    }
  }
  return actions;
}

export function getAvailableActions(
  ctx: GetAvailableActionsContext,
): LdtOrderAction[] {
  const { order, missingInformationMessages, hasValidationErrors, userInfoId } =
    ctx;
  const status = order.status;

  if (status === OrderStatus.Draft) {
    const canEdit = canEditOrder(order, userInfoId);
    return getDraftActions(canEdit);
  }
  if (status === OrderStatus.QualityControlPending) {
    return getQcPendingActions(missingInformationMessages, hasValidationErrors);
  }
  if (status === OrderStatus.QualityControlApproved) {
    return getQcApprovedActions();
  }
  if (status === OrderStatus.LabProcessing) return getLabProcessingActions();
  if (status === OrderStatus.ReportPending) return getReportPendingActions();
  if (
    status === OrderStatus.ReportReady ||
    status === OrderStatus.ReportFaxed
  ) {
    return getReportReadyActions(
      shouldShowFaxTestReportToPathologyLab(order.mostRecentPathologyTrrFax),
    );
  }
  if (status === OrderStatus.ReportFaxPending)
    return getReportFaxPendingActions();
  if (status === OrderStatus.ReportFaxFailed)
    return getReportFaxFailedActions();
  if (status === OrderStatus.PathologyFaxPending)
    return getPathologyFaxPendingActions();
  if (status === OrderStatus.PathologyFaxFailed)
    return getPathologyFaxFailedActions();
  if (status === OrderStatus.SlideImageQualityControlPending) {
    return getSlideImageQualityControlPendingActions(order);
  }
  if (status === OrderStatus.SlideImageQualityControlFailed) {
    return getSlideImageQualityControlFailedActions(order);
  }
  if (
    status === OrderStatus.AiProcessing ||
    status === OrderStatus.AiError ||
    status === OrderStatus.ReportRejected ||
    status === OrderStatus.LabError
  ) {
    return getOtherProcessingStatusActions();
  }
  return [];
}

export function getOrderMissingInfoMessages(
  order: LdtOrderDetailFieldsFragment,
): string[] {
  const messages: string[] = [];
  if (!isCustomerSuccessOrSalesUser()) return [];

  if (isClinicalVariablesMissing(order)) {
    messages.push(CLINICAL_VARIABLES_MISSING_MESSAGE);
  }

  if (isOrderingProviderMissing(order)) {
    messages.push(ORDERING_PROVIDER_MISSING_MESSAGE);
  }

  if (isSpecimenInformationMissing(order)) {
    messages.push(SPECIMEN_INFORMATION_MISSING_MESSAGE);
  }

  messages.push(...getPathologyLabMissingInfoMessages(order));
  messages.push(...getClinicRequiredUploadMissingInfoMessages(order));

  return messages;
}

export async function checkOrderValidationErrors(
  context: CheckOrderValidationErrorsContext,
): Promise<boolean> {
  return hasLdtValidationErrors(
    context.order,
    isCustomerSuccessOrSalesUser(),
    WorkflowType.CLINIC,
    context.order.status === OrderStatus.QualityControlPending,
    context.order.specimenType,
  );
}

/** Show FaxTestReportToPathologyLabMutation when no fax yet, or when status is success/failure/canceled (not pending). */
function shouldShowFaxTestReportToPathologyLab(
  mostRecentPathologyTrrFax: FaxFieldsFragment | null,
): boolean {
  if (!mostRecentPathologyTrrFax) return true;
  return mostRecentPathologyTrrFax.status !== ArteraFaxStatus.Pending;
}
